'use client'

import { OorvaLogo } from '@/components/ui/oorva-logo'
import { cn } from '@/lib/utils'
import { ArrowUpRight, Menu, X } from 'lucide-react'
import Link from 'next/link'
import { usePathname } from 'next/navigation'
import { useEffect, useState } from 'react'

const navItems = [
  { name: 'Experiences', href: '/experiences', match: ['/experiences', '/experience'] },
  { name: 'Destinations', href: '/destinations', match: ['/destinations', '/thanjavur'] },
  { name: 'Weekend Trips', href: '/weekend-trips', match: ['/weekend-trips'] },
  { name: 'For partners', href: '/collaborations', match: ['/collaborations', '/cap', '/riip'] },
  { name: 'Become a host', href: '/host', match: ['/host'] },
]

interface HeaderProps {
  primaryCtaHref?: string
  primaryCtaLabel?: string
  primaryCtaExternal?: boolean
  solid?: boolean
}

export const Header = ({
  primaryCtaHref = 'https://app.oorva.in',
  primaryCtaLabel = 'Explore experiences',
  primaryCtaExternal,
  solid = false,
}: HeaderProps) => {
  const pathname = usePathname()
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)
  const ctaIsExternal = primaryCtaExternal ?? primaryCtaHref.startsWith('http')
  const isActiveItem = (match: string[]) =>
    match.some((prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`))

  useEffect(() => {
    if (!isMobileMenuOpen) return

    const previousOverflow = document.body.style.overflow
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setIsMobileMenuOpen(false)
    }

    document.body.style.overflow = 'hidden'
    window.addEventListener('keydown', closeOnEscape)

    return () => {
      document.body.style.overflow = previousOverflow
      window.removeEventListener('keydown', closeOnEscape)
    }
  }, [isMobileMenuOpen])

  return (
    <header className={cn('fixed inset-x-0 top-0 z-50 border-b border-border/80 bg-background/94 pt-[env(safe-area-inset-top)] text-foreground backdrop-blur-xl', solid && 'shadow-sm')}>
      <div className="site-shell flex h-[4.5rem] items-center justify-between">
        <Link href="/" className="relative z-50 -ml-2 flex min-h-11 items-center" aria-label="Oorva home">
          <OorvaLogo size="md" className="transition-transform duration-300 hover:scale-[1.02]" />
        </Link>

        <nav className="hidden items-center gap-1 rounded-full border border-border bg-card/70 p-1 lg:flex" aria-label="Main navigation">
          {navItems.map((item) => {
            const isActive = isActiveItem(item.match)

            return (
              <Link
                key={item.name}
                href={item.href}
                aria-current={isActive ? 'page' : undefined}
                className={cn(
                  'rounded-full px-4 py-2 text-[0.78rem] font-semibold transition-colors',
                  isActive ? 'bg-primary text-primary-foreground' : 'text-foreground/62 hover:text-foreground',
                )}
              >
                {item.name}
              </Link>
            )
          })}
        </nav>

        <div className="hidden lg:block">
          <Link
            href={primaryCtaHref}
            target={ctaIsExternal ? '_blank' : undefined}
            rel={ctaIsExternal ? 'noopener noreferrer' : undefined}
            className="site-button-primary min-h-11 px-5"
          >
            {primaryCtaLabel}
            <ArrowUpRight className="size-3.5" />
          </Link>
        </div>

        <button
          type="button"
          onClick={() => setIsMobileMenuOpen((open) => !open)}
          className="relative z-50 grid size-11 place-items-center rounded-full border border-border bg-card text-foreground transition active:scale-95 lg:hidden"
          aria-expanded={isMobileMenuOpen}
          aria-controls="mobile-site-navigation"
          aria-label={isMobileMenuOpen ? 'Close menu' : 'Open menu'}
        >
          {isMobileMenuOpen ? <X className="size-5" /> : <Menu className="size-5" />}
        </button>
      </div>

      {isMobileMenuOpen && (
        <div
          id="mobile-site-navigation"
          className="fixed inset-0 z-40 flex h-dvh flex-col bg-background px-5 pb-[max(1.5rem,env(safe-area-inset-bottom))] pt-[calc(6rem+env(safe-area-inset-top))] text-foreground lg:hidden"
        >
          <p className="site-label">Explore Oorva</p>
          <p className="mt-3 max-w-xs text-3xl font-semibold leading-tight tracking-[-0.04em]">
            Travel through people, place and living culture.
          </p>

          <nav className="mt-8 border-t border-border" aria-label="Mobile navigation">
            {navItems.map((item) => {
              const isActive = isActiveItem(item.match)

              return (
                <Link
                  key={item.name}
                  href={item.href}
                  aria-current={isActive ? 'page' : undefined}
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="flex min-h-16 items-center justify-between border-b border-border text-xl font-medium"
                >
                  <span className={isActive ? 'text-brand' : undefined}>{item.name}</span>
                  <ArrowUpRight className="size-4 text-foreground/35" />
                </Link>
              )
            })}
          </nav>

          <div className="mt-auto pt-8">
            <Link
              href={primaryCtaHref}
              target={ctaIsExternal ? '_blank' : undefined}
              rel={ctaIsExternal ? 'noopener noreferrer' : undefined}
              onClick={() => setIsMobileMenuOpen(false)}
              className="site-button-primary w-full text-base"
            >
              {primaryCtaLabel}
              <ArrowUpRight className="size-4" />
            </Link>
          </div>
        </div>
      )}
    </header>
  )
}
